import type { Member, ProjectPerson } from '../../lib/api.js';
import { MoreMenu } from '../ui/MoreMenu.js';
import { adjustedFromDefault, initialsOf, roleLabel, roleMeaning } from './roles.js';

/** A member's name from the platform directory, or null while it cannot be read. */
export function memberName(people: readonly ProjectPerson[] | undefined, userId: number): string | null {
  return people?.find((p) => p.userId === userId)?.displayName ?? null;
}

/**
 * ONE LINE PER PERSON: who they are, the job, and what the job lets them do.
 *
 * The credit sits beside the name as its own chip — it is how the name reads
 * on a call sheet and grants nothing — and the role is the job's name, never
 * the row's value. The access line is the catalogue's summary of the job; when
 * this production changed what the role may do, the line says so rather than
 * repeating a default that is no longer the whole story.
 */
export function MemberList({
  members,
  people,
  me,
  manageRefusal,
  onChangeRole,
  onSeeRole,
}: {
  members: readonly Member[];
  people: readonly ProjectPerson[] | undefined;
  me: number | null;
  manageRefusal: string | null;
  onChangeRole: (member: Member) => void;
  onSeeRole: (role: string) => void;
}) {
  return (
    <ul className="team-member-list" data-testid="team-member-list">
      {members.map((m) => {
        const name = memberName(people, m.userId);
        const shown = name ?? `Member #${String(m.userId)}`;
        const adjusted = adjustedFromDefault(m.authorizationRole, m.permissions);

        return (
          <li key={m.id} className="team-member" data-testid={`member-${m.id}`}>
            <span className="team-avatar" aria-hidden="true">
              {initialsOf(name)}
            </span>
            <div className="team-member-who">
              <span className="team-member-name" data-testid={`member-name-${m.id}`}>
                {shown}
              </span>
              {me !== null && m.userId === me ? (
                <span className="team-you" data-testid={`member-you-${m.id}`}>
                  You
                </span>
              ) : null}
              {m.productionTitle ? (
                <span className="chip" data-testid={`member-credit-${m.id}`}>
                  {m.productionTitle}
                </span>
              ) : null}
              {m.status === 'ACTIVE' ? null : (
                <span className="team-inactive" data-testid={`member-inactive-${m.id}`}>
                  Not active
                </span>
              )}
            </div>
            <span className="team-role" data-testid={`role-${m.id}`}>
              {roleLabel(m.authorizationRole)}
            </span>
            <p className="team-member-access" data-testid={`member-access-${m.id}`}>
              {roleMeaning(m.authorizationRole)}
              {adjusted ? (
                <span className="team-adjusted" data-testid={`member-adjusted-${m.id}`}>
                  Changed for this production
                </span>
              ) : null}
            </p>
            <MoreMenu
              testId={`member-menu-${m.id}`}
              label={`Actions for ${shown}`}
              items={[
                {
                  id: 'change-role',
                  label: 'Change role',
                  testId: `member-change-role-${m.id}`,
                  disabled: manageRefusal !== null,
                  ...(manageRefusal === null ? {} : { hint: manageRefusal }),
                  onSelect: () => {
                    onChangeRole(m);
                  },
                },
                {
                  id: 'see-role',
                  label: 'See what this role can do',
                  testId: `member-see-role-${m.id}`,
                  onSelect: () => {
                    onSeeRole(m.authorizationRole);
                  },
                },
              ]}
            />
          </li>
        );
      })}
    </ul>
  );
}
